import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TenancyModal, ScoreRing } from '../platform/_ui';
import { useOrganization } from './OrganizationRuntimeContext.jsx';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';
import * as api from '../api/org-security.api.js';
import { orgPoliciesApi } from '../api/org-policies.api.js';

const SECURITY_VIEW = 'org.security.view';
const SECURITY_EDIT = 'org.security.configure'; // F-03 fix: was 'org.settings.edit'
const SESSION_PAGE_SIZE = 10;

/* F-10: client-side CIDR validation prevents obviously invalid entries from reaching the API */
const CIDR_V4 = /^(\d{1,3}\.){3}\d{1,3}\/(\d|[12]\d|3[02])$/;
const CIDR_V6 = /^[0-9a-fA-F:]+\/(\d{1,2}|1[01]\d|12[0-8])$/;
const isValidCidr = (v) => CIDR_V4.test(v) || CIDR_V6.test(v);

const SCORE_LEVEL = (s) => {
  if (s >= 90) return { label: 'Excellent',      tone: 'green',  color: 'var(--nx-success)' };
  if (s >= 70) return { label: 'Good',           tone: 'green',  color: 'var(--nx-success)' };
  if (s >= 50) return { label: 'Fair',           tone: 'amber',  color: 'var(--nx-warning)' };
  return             { label: 'Needs Attention', tone: 'red',    color: 'var(--nx-danger)'  };
};

/* ── Extracted sub-components (complexity stays out of main function) ── */

function SecurityScoreCard({ score, level }) { // NOSONAR
  return (
    <div className="nx-cc-chart-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Security Score</div>
      <div style={{ marginBottom: 12 }}>
        <ScoreRing score={score} color={level.color} size={96} />
      </div>
      <Pill tone={level.tone}>{level.label}</Pill>
    </div>
  );
}

function SecurityPolicyCard({ policies, onConfigure }) { // NOSONAR
  const sessionTimeoutMinutes = policies?.sessionTimeoutMinutes ?? 0;
  const passwordRotationDays  = policies?.passwordRotationDays  ?? 0;
  return (
    <div className="nx-cc-chart-card" style={{ padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Security Policy</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--nx-divider)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name="Smartphone" size={15} style={{ color: 'var(--nx-accent)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Multi-Factor Authentication</div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
              Configured in{' '}
              <button onClick={onConfigure} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--nx-accent)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
                Identity &amp; Access
              </button>
            </div>
          </div>
          <Pill tone={policies?.mfaRequired ? 'green' : 'gray'}>{policies?.mfaRequired ? 'Required' : 'Optional'}</Pill>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--nx-divider)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-success-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name="Key" size={15} style={{ color: 'var(--nx-success)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Single Sign-On (SSO)</div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Configured via your identity provider</div>
          </div>
          <Pill tone={policies?.ssoEnabled ? 'green' : 'gray'}>{policies?.ssoEnabled ? 'Enabled' : 'Disabled'}</Pill>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--nx-divider)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-warning-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name="UserPlus" size={15} style={{ color: 'var(--nx-warning)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Auto-Provisioning</div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Automatically provision members via SSO</div>
          </div>
          <Pill tone={policies?.autoProvision ? 'green' : 'gray'}>{policies?.autoProvision ? 'Enabled' : 'Disabled'}</Pill>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--nx-divider)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name="Clock" size={15} style={{ color: 'var(--nx-text-muted)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Session Timeout</div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Idle session expiry policy</div>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{sessionTimeoutMinutes > 0 ? `${sessionTimeoutMinutes}m` : '—'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--nx-divider)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name="RefreshCw" size={15} style={{ color: 'var(--nx-text-muted)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Password Rotation</div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Mandatory password change interval</div>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{passwordRotationDays > 0 ? `${passwordRotationDays}d` : '—'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name="KeyRound" size={15} style={{ color: 'var(--nx-text-muted)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Password Complexity</div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
              {policies?.passwordMinLength ? `Min ${policies.passwordMinLength} chars` : 'No minimum'}{' · '}
              {[
                policies?.passwordRequireUppercase && 'A–Z',
                policies?.passwordRequireLowercase && 'a–z',
                policies?.passwordRequireNumbers   && '0–9',
                policies?.passwordRequireSymbols   && '!@#',
              ].filter(Boolean).join(' · ') || 'no complexity rules'}
            </div>
          </div>
          <Pill tone={(policies?.passwordMinLength || policies?.passwordRequireUppercase || policies?.passwordRequireNumbers || policies?.passwordRequireSymbols) ? 'green' : 'gray'}>
            {(policies?.passwordMinLength || policies?.passwordRequireUppercase || policies?.passwordRequireNumbers || policies?.passwordRequireSymbols) ? 'Configured' : 'None'}
          </Pill>
        </div>
      </div>
    </div>
  );
}

function IpAllowlistCard({ allowlist, canEdit, newIp, ipLabel, addingIp, removingIp, cidrError, onNewIpChange, onIpLabelChange, onAdd, onRemove }) { // NOSONAR
  return (
    <div className="nx-cc-chart-card" style={{ padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>IP Allowlist</div>
      {canEdit && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="nx-input" style={{ flex: 1, fontSize: 13, borderColor: cidrError ? 'var(--nx-danger)' : undefined }} placeholder="CIDR e.g. 192.168.1.0/24" value={newIp} onChange={e => onNewIpChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onAdd()} aria-invalid={!!cidrError} aria-describedby={cidrError ? 'cidr-error' : undefined} />
            <input className="nx-input" style={{ width: 120, fontSize: 13 }} placeholder="Label (opt.)" value={ipLabel} onChange={e => onIpLabelChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onAdd()} />
            <Btn variant="primary" size="sm" icon="Plus" disabled={addingIp || !newIp?.trim()} onClick={onAdd}>Add</Btn>{/* NOSONAR */}
          </div>
          {cidrError && <div id="cidr-error" role="alert" style={{ fontSize: 11, color: 'var(--nx-danger)', marginTop: 4 }}>{cidrError}</div>}
        </div>
      )}
      {allowlist.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 12px', borderRadius: 8, background: 'var(--nx-accent-tint)', fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <NexusIcon name="Globe" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
          <span>No IP restrictions — all IPs are allowed.</span>
        </div>
      ) : (
        <table className="nx-table">
          <thead><tr><th scope="col">CIDR Range</th><th scope="col">Label</th><th scope="col">Added</th>{canEdit && <th scope="col"></th>}</tr></thead>
          <tbody>
            {allowlist.map((e, i) => (
              <tr key={e.id ?? i}>
                <td><code style={{ fontSize: 12, background: 'var(--nx-surface-alt)', padding: '2px 6px', borderRadius: 4 }}>{e.cidr ?? e.ip}</code></td>
                <td style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{e.label ?? '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{e.createdAt ? new Date(e.createdAt).toLocaleDateString() : '—'}</td>
                {canEdit && (
                  <td>
                    <button disabled={removingIp === e.id} onClick={() => onRemove(e.id)} style={{ background: 'transparent', border: '1px solid color-mix(in srgb, var(--nx-danger) 40%, transparent)', borderRadius: 4, color: 'var(--nx-danger)', fontSize: 11, padding: '2px 8px', cursor: removingIp === e.id ? 'wait' : 'pointer', opacity: removingIp === e.id ? 0.5 : 1 }}>
                      {removingIp === e.id ? '…' : 'Remove'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SessionsCard({ sessions, sessPage, totalSessPages, pagedSessions, canEdit, revokingId, onRevoke, onPageChange }) { // NOSONAR
  return (
    <div className="nx-cc-chart-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Active Sessions</div>
        <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{sessions.length} active</span>
      </div>
      {sessions.length === 0 ? (
        <div className="nx-empty" style={{ padding: '24px 0' }}><NexusIcon name="Users" size={20} /><span>No active sessions.</span></div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pagedSessions.map((s, i) => (
              <div key={s.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--nx-divider)' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, fontWeight: 700, color: 'var(--nx-accent)' }}>
                  {(s.user?.name ?? s.user?.email ?? s.email ?? '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.user?.name ?? s.user?.email ?? s.email ?? '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{s.device ?? s.browser ?? s.userAgent ?? '—'}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <code style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{s.ip ?? '—'}</code>
                  <div style={{ fontSize: 10, color: 'var(--nx-text-faint)' }}>{s.lastActiveAt ? new Date(s.lastActiveAt).toLocaleDateString() : '—'}</div>
                </div>
                {canEdit && s.id && (
                  <button disabled={revokingId === s.id} onClick={() => onRevoke(s.id)} style={{ flexShrink: 0, background: 'transparent', border: '1px solid color-mix(in srgb, var(--nx-danger) 40%, transparent)', borderRadius: 5, color: 'var(--nx-danger)', fontSize: 11, padding: '2px 8px', cursor: revokingId === s.id ? 'wait' : 'pointer', opacity: revokingId === s.id ? 0.5 : 1 }}>
                    {revokingId === s.id ? '…' : 'Revoke'}
                  </button>
                )}
              </div>
            ))}
          </div>
          {totalSessPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--nx-text-muted)' }}>
              <button className="nx-btn-ghost-sm" disabled={sessPage === 1} onClick={() => onPageChange(p => p - 1)}>← Prev</button>
              <span>Page {sessPage} of {totalSessPages}</span>
              <button className="nx-btn-ghost-sm" disabled={sessPage >= totalSessPages} onClick={() => onPageChange(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────── */
export default function OrgSecurityCenter({ embedded = false }) { // NOSONAR
  const { orgId } = useParams();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const toast     = useToast();
  const org       = useOrganization();
  const perm      = usePermissions();
  const denied    = !perm.has(SECURITY_VIEW) && !perm.isSA;
  const canEdit   = perm.has(SECURITY_EDIT)  || perm.isSA;

  const [security,    setSecurity]    = useState(null);
  const [policies,    setPolicies]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [sessions,    setSessions]    = useState([]);
  const [sessPage,    setSessPage]    = useState(1);
  const [allowlist,   setAllowlist]   = useState([]);
  const [newIp,       setNewIp]       = useState('');
  const [ipLabel,     setIpLabel]     = useState('');
  const [addingIp,    setAddingIp]    = useState(false);
  const [removingIp,  setRemovingIp]  = useState(null);
  const [revokingId,  setRevokingId]  = useState(null);
  const [cidrError,   setCidrError]   = useState('');
  const [revokeConfirm,   setRevokeConfirm]   = useState(null);
  const [removeIpConfirm, setRemoveIpConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, sess, al, pol] = await Promise.all([
        api.getSecurityCenter(orgId),
        api.getSessions(orgId).catch(() => ({})),
        api.getIpAllowlist(orgId).catch(() => ({})),
        orgPoliciesApi.get(orgId).catch(() => null),
      ]);
      setSecurity(s);
      setSessions(Array.isArray(sess) ? sess : (sess?.sessions ?? []));
      setAllowlist(Array.isArray(al) ? al : (al?.entries ?? []));
      setPolicies(pol);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load security data' });
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => { load(); }, [load]);

  const handleAddIp = useCallback(async () => {
    if (!newIp.trim() || !canEdit) return;
    if (!isValidCidr(newIp.trim())) {
      setCidrError('Invalid CIDR — expected format: 192.168.1.0/24 or 2001:db8::/32');
      return;
    }
    setCidrError('');
    setAddingIp(true);
    try {
      const entry = await api.addIpAllowlist(orgId, { cidr: newIp.trim(), label: ipLabel.trim() || undefined });
      setAllowlist(prev => [...prev, entry]);
      setNewIp(''); setIpLabel('');
      toast({ tone: 'success', icon: 'Check', title: 'IP range added' });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to add IP range' });
    } finally { setAddingIp(false); }
  }, [newIp, ipLabel, canEdit, orgId, toast]);

  const handleRemoveIp = useCallback(async (entryId) => {
    if (!canEdit) return;
    setRemovingIp(entryId);
    try {
      await api.removeIpAllowlist(orgId, entryId);
      setAllowlist(prev => prev.filter(e => e.id !== entryId));
      toast({ tone: 'success', icon: 'Check', title: 'IP range removed' });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to remove IP range' });
    } finally { setRemovingIp(null); }
  }, [canEdit, orgId, toast]);

  const handleRevokeSession = useCallback(async (sessionId) => {
    if (!canEdit) return;
    setRevokingId(sessionId);
    try {
      await api.revokeSession(orgId, sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      toast({ tone: 'success', icon: 'Check', title: 'Session revoked' });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to revoke session' });
    } finally { setRevokingId(null); }
  }, [canEdit, orgId, toast]);

  if (!orgId) return null;
  if (denied)  return <DeniedState capability={SECURITY_VIEW} />;

  const score          = security?.securityScore ?? 0;
  const level          = SCORE_LEVEL(score);
  let scoreColor;
  if (score >= 70) scoreColor = 'green';
  else if (score >= 50) scoreColor = 'amber';
  else scoreColor = 'red';
  const totalSessPages = Math.ceil(sessions.length / SESSION_PAGE_SIZE) || 1;
  const pagedSessions  = sessions.slice((sessPage - 1) * SESSION_PAGE_SIZE, sessPage * SESSION_PAGE_SIZE);

  const content = (
    <>
      {loading && <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="Loader" size={20} /><span>Loading security data…</span></div>}
      {!loading && !security && <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="AlertCircle" size={20} /><span>Could not load security data.</span></div>}
      {!loading && security && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="nx-cc-stat-grid nx-kpi-4">
            <StatCard icon="ShieldCheck"  label="Security Score"  value={score}                                  sub={level.label}            color={scoreColor} />
            <StatCard icon="Smartphone"   label="MFA"             value={policies?.mfaRequired ? 'Required' : 'Optional'} sub="org-wide policy" color={policies?.mfaRequired ? 'green' : 'amber'} />
            <StatCard icon="Key"          label="SSO"             value={policies?.ssoEnabled ? 'Enabled' : 'Disabled'} sub="single sign-on" color={policies?.ssoEnabled ? 'green' : 'gray'} />
            <StatCard icon="Activity"     label="Active Sessions" value={sessions.length}                        sub="across all members"     color="blue" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20 }}>
            <SecurityScoreCard score={score} level={level} />
            <SecurityPolicyCard policies={policies} onConfigure={() => navigate(`/organization/${orgId}/iam?tab=authentication`)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <IpAllowlistCard allowlist={allowlist} canEdit={canEdit} newIp={newIp} ipLabel={ipLabel} addingIp={addingIp} removingIp={removingIp} cidrError={cidrError} onNewIpChange={v => { setCidrError(''); setNewIp(v); }} onIpLabelChange={setIpLabel} onAdd={handleAddIp} onRemove={id => setRemoveIpConfirm(id)} />
            <SessionsCard sessions={sessions} sessPage={sessPage} totalSessPages={totalSessPages} pagedSessions={pagedSessions} canEdit={canEdit} revokingId={revokingId} onRevoke={id => setRevokeConfirm(id)} onPageChange={setSessPage} />
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return <div className="nx-iam-section" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>{content}</div>;

  return (
    <>
      <div className="nx-cc-page">
        <PageHead
          title="Security Center"
          sub={`Security posture, sessions, and policy for ${org?.name ?? 'Organization'}`}
          back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
          actions={<Btn variant="ghost" icon="RefreshCw" disabled={loading} onClick={load}>Refresh</Btn>}
        />
        {content}
      </div>

      {revokeConfirm && (
        <TenancyModal title="Revoke Session?" icon="LogOut" danger onClose={() => setRevokeConfirm(null)}>
          <p className="nx-confirm-body">This will immediately sign out the selected user. They will need to log in again to continue working.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setRevokeConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="LogOut" onClick={() => { handleRevokeSession(revokeConfirm); setRevokeConfirm(null); }}>Revoke Session</Btn>
          </div>
        </TenancyModal>
      )}

      {removeIpConfirm && (
        <TenancyModal title="Remove IP Range?" icon="ShieldOff" danger onClose={() => setRemoveIpConfirm(null)}>
          <p className="nx-confirm-body">Removing this IP range will allow traffic from that network immediately. Verify this is intentional before proceeding.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setRemoveIpConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={() => { handleRemoveIp(removeIpConfirm); setRemoveIpConfirm(null); }}>Remove Range</Btn>
          </div>
        </TenancyModal>
      )}
    </>
  );
}
